import { useEffect } from 'react'
import type { NextPage } from 'next'
import { paths } from '@/constants/paths'
import { RegionContextProvider } from '@/contexts/RegionContext'
import { Seo } from '@/layout/Seo'
import { getGenericStaticPaths } from '@/utils/GetGenericStaticPaths'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { writeToSessionStorage } from '@/utils/session-storage'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { SubmitView } from '@/views/SubmitView'

interface SubmitPageProps {
  locale: string
}

const SubmitPage: NextPage<SubmitPageProps> = ({ locale }) => {
  const { t } = useTranslate('create')
  const url = `${getBasePath(locale)}${paths.submit.index}`
  const { query } = useSafeRouter()
  const title = t(
    'angelStudiosCreateMetaTitle',
    'Angel Studios: Shape The Future of Entertainment With Your Film Or Show',
  )
  const description = t(
    'angelStudiosCreateMetaDescription',
    'Submit your TV show or movie to be considered by the Angel Guild. Receive audience votes, feedback on your submission, and see if your project scores high enough to be considered for distribution by Angel Studios.',
  )

  useEffect(() => {
    if (query.promoCode) {
      writeToSessionStorage('torchSubmissionPromoCode', getItemOrFirstEntry(query.promoCode))
    }
  }, [query.promoCode])

  return (
    <>
      <Seo
        canonical={url}
        description={description}
        openGraph={{ title, description, url }}
        path={paths.submit.index}
        title={title}
      />
      <RegionContextProvider>
        <SubmitView />
      </RegionContextProvider>
    </>
  )
}

export const getStaticPaths = getGenericStaticPaths

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/submit/index.tsx' },
  async ({ locale, preview }) => {
    return {
      props: {
        locale,
        preview,
        ...(await loadTranslations(locale, ['common', 'create'])),
      },
    }
  },
)

export default SubmitPage
